import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Heart } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/programs', label: 'Programs' },
    { path: '/events', label: 'Events' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/dana-schedule', label: 'Daily Dāna' },
    { path: '/teachings', label: 'Teachings' },
    { path: '/buddha-life', label: "Buddha's Life" },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 pointer-events-none"
    >
      <div
        className={`pointer-events-auto mx-auto transition-all duration-500 ease-out
          ${isScrolled ? 'mt-2 md:mt-3' : 'mt-3 md:mt-5'}
          ${isScrolled ? 'max-w-6xl px-3 md:px-6' : 'max-w-7xl px-3 md:px-8'}`}
      >
        <div
          className="relative rounded-2xl md:rounded-full overflow-visible"
          style={{
            background: isScrolled
              ? 'rgba(255,255,255,0.14)'
              : 'rgba(255,255,255,0.10)',
            backdropFilter: 'blur(22px) saturate(180%)',
            WebkitBackdropFilter: 'blur(22px) saturate(180%)',
            border: '1px solid rgba(255,255,255,0.28)',
            boxShadow:
              '0 10px 40px -12px rgba(15, 40, 71, 0.35), 0 1px 0 rgba(255,255,255,0.35) inset',
          }}
        >
          {/* soft inner sheen */}
          <div
            className="pointer-events-none absolute inset-0 rounded-2xl md:rounded-full"
            style={{
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 60%)',
            }}
          />

          <div className="relative flex items-center justify-between h-16 md:h-[68px] px-4 md:px-6">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_alabama-viharaya/artifacts/7npgfzob_AL%20LOGO.png"
                alt="Alabama Buddhist Viharaya Logo"
                className="h-10 w-auto transition-transform duration-500 group-hover:scale-105"
              />
              <div className="hidden md:block leading-tight">
                <div className="text-[9px] tracking-[0.3em] uppercase text-white/70">Alabama</div>
                <div className="font-serif-display text-[15px] text-white drop-shadow-sm">Buddhist Vihāra</div>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-0.5">
              {navLinks.map((link) => {
                const active = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                    className={`relative px-3 py-2 text-[12.5px] tracking-wide rounded-full transition-all duration-300 ${
                      active
                        ? 'text-white bg-white/15'
                        : 'text-white/85 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <Link
                to="/donate"
                data-testid="nav-donate-btn"
                className="ml-2 inline-flex items-center gap-2 bg-white/95 hover:bg-white text-[#0f2847] px-4 py-2 rounded-full text-[12.5px] font-semibold tracking-wide transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                style={{ boxShadow: '0 6px 18px -6px rgba(255,255,255,0.4)' }}
              >
                <Heart size={13} />
                Donate
              </Link>
            </nav>

            {/* Mobile toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-full text-white hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          {/* Mobile Nav — animated glass panel */}
          <div
            data-testid="mobile-nav"
            className={`lg:hidden overflow-hidden transition-all duration-500 ease-out ${
              isMobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-4 pb-4 pt-1">
              <div className="h-px bg-white/20 mb-3" />
              <div className="flex flex-col gap-0.5">
                {navLinks.map((link) => {
                  const active = location.pathname === link.path;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`px-4 py-2.5 text-[13px] tracking-wide rounded-xl transition-all ${
                        active
                          ? 'text-white bg-white/15'
                          : 'text-white/85 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      {link.label}
                    </Link>
                  );
                })}
                <Link
                  to="/donate"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="mt-2 inline-flex items-center justify-center gap-2 bg-white text-[#0f2847] px-4 py-2.5 rounded-full text-[13px] font-semibold"
                >
                  <Heart size={13} /> Donate
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* soft global drop shadow beneath the floating nav so it "lifts" above hero */}
      <div
        className={`pointer-events-none absolute left-1/2 -translate-x-1/2 w-[92%] max-w-6xl h-3 -bottom-3 blur-xl rounded-full transition-opacity duration-500 ${
          isScrolled ? 'opacity-70' : 'opacity-40'
        }`}
        style={{ background: 'rgba(15,40,71,0.35)' }}
      />
    </header>
  );
};

export default Header;
